import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Inbox, Gavel, FileText, Radio, Send, ChevronUp, ChevronDown } from "lucide-react";
import { ImportExport } from "../components/ImportExport";

const TABS = ["Approvals", "Intake", "Governance", "Publishing", "Report Builder"];

function ApprovalCard({ a, onReview }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const loadDetail = () => api.get(`/approvals/${a.id}/detail`).then((r) => setDetail(r.data)).catch(() => {});
  const toggle = () => { const n = !open; setOpen(n); if (n) loadDetail(); };
  const addNote = async () => {
    if (!note.trim()) return;
    try { await api.post(`/approvals/${a.id}/notes`, { text: note }); setNote(""); loadDetail(); toast.success("Note added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const payload = a.payload || {};
  const before = a.before || {};
  return (
    <div data-testid={`approval-${a.id}`} className="rounded-2xl border border-[#DCE2EA] p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#1F2733]">{a.change_summary}</div>
          <div className="text-[11px] text-[#98A2AF] mt-0.5">Submitted by {a.submitted_by_name} · {new Date(a.created_at).toLocaleString()}</div>
          {a.submitter_note && <div className="mt-2 text-xs text-[#1F2733] bg-[#F4F6FA] border-l-2 border-[#BA0C2F] rounded-r-lg px-3 py-2" data-testid={`approval-submitter-note-${a.id}`}><span className="font-semibold text-[#5A687C]">Submitter note: </span>{a.submitter_note}</div>}
          <div className="mt-2 space-y-1">
            {Object.entries(payload).filter(([k]) => k !== "updated_at").map(([k, v]) => (
              <div key={k} className="text-xs flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-[#5A687C] capitalize w-28 shrink-0">{k.replace(/_/g, " ")}</span>
                {before[k] !== undefined && before[k] !== null && String(before[k]) !== "" && (
                  <><span className="text-[#98A2AF] line-through bg-[#F4F6FA] rounded-full px-2 py-0.5">{String(before[k])}</span><span className="text-[#98A2AF]">→</span></>
                )}
                <span className="text-[#1F2733] bg-[#FBEAED] border border-[#F1B9C3] rounded-full px-2.5 py-0.5">{String(v)}</span>
              </div>
            ))}
          </div>
          <button data-testid={`approval-expand-${a.id}`} onClick={toggle} className="mt-2 text-xs font-semibold text-[#BA0C2F] hover:underline">{open ? "Hide" : "Timeline & internal notes"}</button>
        </div>
        {a.status === "pending" ? (
          <div className="flex gap-2 shrink-0">
            <button data-testid={`approve-${a.id}`} onClick={() => onReview(a.id, "approved")} className="inline-flex items-center gap-1 bg-[#2E8B57] hover:opacity-90 text-white rounded-full px-3.5 py-1.5 text-sm font-semibold"><CheckCircle2 className="h-4 w-4" /> Approve</button>
            <button data-testid={`reject-${a.id}`} onClick={() => onReview(a.id, "rejected")} className="inline-flex items-center gap-1 bg-[#BA0C2F] hover:opacity-90 text-white rounded-full px-3.5 py-1.5 text-sm font-semibold"><XCircle className="h-4 w-4" /> Reject</button>
          </div>
        ) : <span className={`text-xs font-semibold px-3 py-1 rounded-full h-fit ${a.status === "approved" ? "bg-[#ECF8F1] text-[#1A5633]" : "bg-[#FDF2F4] text-[#8A0822]"}`}>{a.status}</span>}
      </div>
      {open && (
        <div className="mt-4 pt-4 border-t border-[#DCE2EA] grid md:grid-cols-2 gap-4" data-testid={`approval-detail-${a.id}`}>
          <div>
            <div className="text-xs font-bold uppercase text-[#5A687C] mb-2">Status timeline</div>
            <div className="space-y-2">
              {(detail?.events || []).map((ev) => (
                <div key={ev.id} className="flex gap-2 text-xs">
                  <span className={`mt-0.5 h-2 w-2 rounded-full shrink-0 ${ev.status === "approved" ? "bg-[#2E8B57]" : ev.status === "rejected" ? "bg-[#BA0C2F]" : "bg-[#98A2AF]"}`} />
                  <div><div className="font-semibold text-[#1F2733] capitalize">{ev.status}</div><div className="text-[#98A2AF]">{ev.actor_name} · {new Date(ev.timestamp).toLocaleString()}</div>{ev.note && <div className="text-[#5A687C] mt-0.5">"{ev.note}"</div>}</div>
                </div>
              ))}
              {detail && (detail.events || []).length === 0 && <div className="text-xs text-[#98A2AF]">No events recorded.</div>}
            </div>
          </div>
          <div>
            <div className="text-xs font-bold uppercase text-[#5A687C] mb-2">Internal PMO notes</div>
            <div className="space-y-2 mb-2">
              {(detail?.notes || []).map((n) => (
                <div key={n.id} className="text-xs bg-[#F4F6FA] rounded-lg px-3 py-2"><span className="font-semibold text-[#1F2733]">{n.user_name}</span><span className="text-[#98A2AF]"> · {new Date(n.created_at).toLocaleString()}</span><div className="text-[#1F2733] mt-0.5">{n.text}</div></div>
              ))}
              {detail && (detail.notes || []).length === 0 && <div className="text-xs text-[#98A2AF]">No internal notes yet.</div>}
            </div>
            <div className="flex gap-2">
              <input data-testid={`approval-note-input-${a.id}`} value={note} onChange={(e) => setNote(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addNote()} placeholder="Add internal note…" className="flex-1 px-3 py-1.5 rounded-lg border border-[#DCE2EA] text-xs" />
              <button data-testid={`approval-note-btn-${a.id}`} onClick={addNote} className="bg-[#1F2733] text-white rounded-lg px-3 text-xs font-semibold">Post</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function PMO() {
  const [tab, setTab] = useState("Approvals");
  const [approvals, setApprovals] = useState([]);
  const [intake, setIntake] = useState([]);
  const [gov, setGov] = useState([]);
  const [ws, setWs] = useState([]);
  const [narrative, setNarrative] = useState("");
  const [sections, setSections] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", requested_owner: "", value_score: 3, risk_score: 3 });
  const [govForm, setGovForm] = useState({ title: "", date: "", decisions: "", action_items: "" });

  const load = () => {
    api.get("/approvals").then((r) => setApprovals(r.data)).catch(() => {});
    api.get("/intake").then((r) => setIntake(r.data)).catch(() => {});
    api.get("/governance").then((r) => setGov(r.data)).catch(() => {});
    api.get("/workstreams").then((r) => setWs(r.data)).catch(() => {});
    api.get("/report").then((r) => setNarrative(r.data.narrative || "")).catch(() => {});
    api.get("/report/sections").then((r) => setSections(r.data.sections)).catch(() => {});
  };
  useEffect(load, []);

  const moveSection = (i, dir) => { const arr = [...sections]; const j = i + dir; if (j < 0 || j >= arr.length) return; [arr[i], arr[j]] = [arr[j], arr[i]]; setSections(arr); };
  const toggleSection = (i) => setSections(sections.map((s, idx) => idx === i ? { ...s, enabled: !s.enabled } : s));
  const saveSections = async () => { try { await api.post("/report/sections", { sections }); toast.success("Report layout saved"); } catch (e) { toast.error(apiErr(e)); } };

  const review = async (aid, status) => {
    const comments = status === "rejected" ? prompt("Reason for rejection (optional):") || "" : "";
    try { await api.post(`/approvals/${aid}/review`, { status, comments }); toast.success(`Request ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const reviewIntake = async (iid, status) => {
    try { await api.post(`/intake/${iid}/review`, { status, comments: "" }); toast.success(`Intake ${status}`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submitIntake = async () => {
    if (!form.title) return toast.error("Title required");
    try { await api.post("/intake", form); toast.success("Intake submitted"); setForm({ title: "", description: "", requested_owner: "", value_score: 3, risk_score: 3 }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const submitGov = async () => {
    if (!govForm.title || !govForm.date) return toast.error("Title and date required");
    try { await api.post("/governance", govForm); toast.success("Meeting logged"); setGovForm({ title: "", date: "", decisions: "", action_items: "" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const togglePublish = async (id) => { try { await api.post(`/workstreams/${id}/publish`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleAuto = async (id) => { try { await api.post(`/workstreams/${id}/auto-approve`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const toggleCompliance = async (id) => { try { await api.post(`/workstreams/${id}/compliance-scope`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const saveNarrative = async () => { try { await api.post("/report/narrative", { narrative }); toast.success("Narrative saved"); } catch (e) { toast.error(apiErr(e)); } };

  const pending = approvals.filter((a) => a.status === "pending");

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl lg:text-3xl font-extrabold text-[#1F2733]">PMO Control Center</h1>
        <p className="text-[#5A687C] mt-1">Govern the program — intake, approvals, publishing, and executive reporting.</p>
      </div>

      <div className="flex items-center gap-1.5 bg-[#F4F6FA] p-1.5 rounded-full border border-[#DCE2EA] w-fit overflow-x-auto">
        {TABS.map((t) => (
          <button key={t} data-testid={`pmo-tab-${t.toLowerCase().replace(/\s+/g, "-")}`} onClick={() => setTab(t)}
            className={`px-4 py-1.5 rounded-full text-sm whitespace-nowrap transition-all ${tab === t ? "bg-[#BA0C2F] text-white font-semibold" : "text-[#5A687C] hover:bg-white/70 font-medium"}`}>
            {t}{t === "Approvals" && pending.length > 0 ? ` (${pending.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "Approvals" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-4"><Gavel className="h-5 w-5 text-[#BA0C2F]" /> Approvals queue</h2>
          <div className="space-y-3">
            {approvals.length === 0 && <div className="text-sm text-[#5A687C]">No change requests.</div>}
            {approvals.map((a) => (
              <ApprovalCard key={a.id} a={a} onReview={review} />
            ))}
          </div>
        </div>
      )}

      {tab === "Intake" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
            <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-4"><Inbox className="h-5 w-5 text-[#BA0C2F]" /> New initiative intake</h2>
            <div className="space-y-3">
              <input data-testid="intake-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Initiative title" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <textarea data-testid="intake-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description / business case" rows={3} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <input value={form.requested_owner} onChange={(e) => setForm({ ...form, requested_owner: e.target.value })} placeholder="Requested owner" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-[#5A687C]">Value (1-5)<input type="number" min="1" max="5" value={form.value_score} onChange={(e) => setForm({ ...form, value_score: +e.target.value })} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#DCE2EA] text-sm" /></label>
                <label className="text-xs text-[#5A687C]">Risk (1-5)<input type="number" min="1" max="5" value={form.risk_score} onChange={(e) => setForm({ ...form, risk_score: +e.target.value })} className="w-full mt-1 px-3 py-2 rounded-xl border border-[#DCE2EA] text-sm" /></label>
              </div>
              <button data-testid="intake-submit-btn" onClick={submitIntake} className="w-full bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full py-2.5 font-semibold">Submit for PMO review</button>
            </div>
          </div>
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[#1F2733]">Intake requests</h2>
              <ImportExport collection="intake" onDone={load} />
            </div>
            <div className="space-y-3">
              {intake.length === 0 && <div className="text-sm text-[#5A687C]">No intake requests.</div>}
              {intake.map((i) => (
                <div key={i.id} className="rounded-2xl border border-[#DCE2EA] p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div><div className="text-sm font-semibold text-[#1F2733]">{i.title}</div><div className="text-[11px] text-[#98A2AF]">{i.submitted_by_name} · V{i.value_score}/R{i.risk_score}</div></div>
                    {i.status === "pending" ? (
                      <div className="flex gap-1.5">
                        <button onClick={() => reviewIntake(i.id, "approved")} className="text-[#2E8B57]"><CheckCircle2 className="h-5 w-5" /></button>
                        <button onClick={() => reviewIntake(i.id, "rejected")} className="text-[#BA0C2F]"><XCircle className="h-5 w-5" /></button>
                      </div>
                    ) : <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${i.status === "approved" ? "bg-[#ECF8F1] text-[#1A5633]" : "bg-[#FDF2F4] text-[#8A0822]"}`}>{i.status}</span>}
                  </div>
                  <p className="text-xs text-[#5A687C] mt-1">{i.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "Governance" && (
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
            <h2 className="font-bold text-[#1F2733] mb-4">Log steering committee meeting</h2>
            <div className="space-y-3">
              <input value={govForm.title} onChange={(e) => setGovForm({ ...govForm, title: e.target.value })} placeholder="Meeting title" className="w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <input type="date" value={govForm.date} onChange={(e) => setGovForm({ ...govForm, date: e.target.value })} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <textarea value={govForm.decisions} onChange={(e) => setGovForm({ ...govForm, decisions: e.target.value })} placeholder="Decisions" rows={2} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <textarea value={govForm.action_items} onChange={(e) => setGovForm({ ...govForm, action_items: e.target.value })} placeholder="Action items" rows={2} className="w-full px-4 py-2.5 rounded-xl border border-[#DCE2EA] text-sm" />
              <button onClick={submitGov} className="w-full bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full py-2.5 font-semibold">Log meeting</button>
            </div>
          </div>
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-bold text-[#1F2733]">Governance cadence</h2>
              <ImportExport collection="governance" onDone={load} />
            </div>
            <div className="space-y-3">
              {gov.length === 0 && <div className="text-sm text-[#5A687C]">No meetings logged.</div>}
              {gov.map((g) => (
                <div key={g.id} className="rounded-2xl border border-[#DCE2EA] p-4">
                  <div className="text-sm font-semibold text-[#1F2733]">{g.title}</div>
                  <div className="text-[11px] text-[#98A2AF]">{g.date}</div>
                  {g.decisions && <div className="text-xs text-[#5A687C] mt-1"><b>Decisions:</b> {g.decisions}</div>}
                  {g.action_items && <div className="text-xs text-[#5A687C] mt-0.5"><b>Actions:</b> {g.action_items}</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "Publishing" && (
        <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
          <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-1"><Radio className="h-5 w-5 text-[#BA0C2F]" /> Publishing control</h2>
          <p className="text-xs text-[#5A687C] mb-4">Executives only see published workstreams. Toggle auto-approve to trust a workstream owner's edits.</p>
          <div className="space-y-2">
            {ws.map((w) => (
              <div key={w.id} className="flex items-center gap-3 p-3 rounded-xl hover:bg-[#F4F6FA]">
                <span className="text-xs font-bold text-[#5A687C] w-14">{w.code}</span>
                <div className="flex-1 min-w-0"><div className="text-sm font-medium text-[#1F2733] truncate">{w.title}</div></div>
                <button data-testid={`compliance-toggle-${w.code.toLowerCase()}`} onClick={() => toggleCompliance(w.id)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${w.compliance_scope ? "bg-[#FDF2F4] text-[#8A0822] border-[#F8C4CC]" : "bg-white text-[#5A687C] border-[#DCE2EA] hover:border-[#BA0C2F]"}`}>
                  {w.compliance_scope ? "● In Compliance view" : "Add to Compliance"}
                </button>
                <label className="flex items-center gap-2 text-xs text-[#5A687C] cursor-pointer">
                  <input type="checkbox" checked={!!w.auto_approve} onChange={() => toggleAuto(w.id)} /> Auto-approve
                </label>
                <button data-testid={`publish-toggle-${w.code.toLowerCase()}`} onClick={() => togglePublish(w.id)}
                  className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-all ${w.published ? "bg-[#ECF8F1] text-[#1A5633] border border-[#A3E2BB]" : "bg-[#F1F3F6] text-[#4A525D] border border-[#D2D7DF]"}`}>
                  {w.published ? "● Published" : "○ Draft"}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "Report Builder" && (
        <div className="space-y-6">
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6" data-testid="report-sections-builder">
            <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-1"><FileText className="h-5 w-5 text-[#BA0C2F]" /> Board report sections</h2>
            <p className="text-xs text-[#5A687C] mb-4">Choose which sections appear — and in what order — when you save a snapshot for the board pack.</p>
            <div className="space-y-2">
              {sections.map((s, i) => (
                <div key={s.key} data-testid={`section-row-${s.key}`} className="flex items-center gap-3 p-3 rounded-xl border border-[#DCE2EA] bg-[#F4F6FA]/40">
                  <span className="text-xs font-bold text-[#98A2AF] w-6">{i + 1}</span>
                  <div className="flex-1 text-sm font-medium text-[#1F2733]">{s.label}</div>
                  <label className="flex items-center gap-2 text-xs text-[#5A687C] cursor-pointer">
                    <input type="checkbox" data-testid={`section-toggle-${s.key}`} checked={s.enabled} onChange={() => toggleSection(i)} /> Show
                  </label>
                  <button data-testid={`section-up-${s.key}`} onClick={() => moveSection(i, -1)} disabled={i === 0} className="text-[#5A687C] hover:text-[#BA0C2F] disabled:opacity-30"><ChevronUp className="h-4 w-4" /></button>
                  <button data-testid={`section-down-${s.key}`} onClick={() => moveSection(i, 1)} disabled={i === sections.length - 1} className="text-[#5A687C] hover:text-[#BA0C2F] disabled:opacity-30"><ChevronDown className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <button data-testid="save-sections-btn" onClick={saveSections} className="mt-4 inline-flex items-center gap-1.5 bg-[#1F2733] hover:bg-[#0f141b] text-white rounded-full px-5 py-2.5 font-semibold text-sm">Save section layout</button>
          </div>
          <div className="bg-white rounded-[18px] border border-[#DCE2EA] p-6">
            <h2 className="font-bold text-[#1F2733] flex items-center gap-2 mb-1"><FileText className="h-5 w-5 text-[#BA0C2F]" /> Executive report narrative</h2>
            <p className="text-xs text-[#5A687C] mb-4">Add narrative commentary that appears on the published executive report.</p>
            <textarea data-testid="report-narrative" value={narrative} onChange={(e) => setNarrative(e.target.value)} rows={8} placeholder="e.g. This period, the program remained largely on track…" className="w-full px-4 py-3 rounded-xl border border-[#DCE2EA] text-sm" />
            <button data-testid="save-narrative-btn" onClick={saveNarrative} className="mt-3 inline-flex items-center gap-1.5 bg-[#BA0C2F] hover:bg-[#8A0822] text-white rounded-full px-5 py-2.5 font-semibold"><Send className="h-4 w-4" /> Save narrative</button>
          </div>
        </div>
      )}
    </div>
  );
}
